/**
 * Drawer accessibility remediation (WCAG 2.1 AA)
 *
 * Added July 2026 as part of the Simply Dresses WCAG remediation.
 *
 * Covers:
 *   2.1.1 / 2.1.2  ESC closes the mobile navigation drawer
 *   2.4.3          Focus moves into the drawer on open and returns to the
 *                  trigger on close, and is trapped while the drawer is open
 *   4.1.2          The menu trigger reports its expanded state
 *
 * This lives in its own asset rather than in components.js so it survives
 * theme updates from the vendor bundle. It drives off the drawer's
 * data-animation-state attribute, which is what the theme CSS already uses
 * to show and hide the drawer (components.css.liquid: .nav-drawer is
 * display:none by default, display:block at [data-animation-state=open]).
 */
(function () {
  'use strict';

  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  var drawer = document.querySelector('.nav-drawer');
  var trigger = document.querySelector('[data-menu-button]');
  var closeButton = document.querySelector('[data-menu-close-button]');

  if (!drawer || !trigger) return;

  var lastFocused = null;

  function isOpen() {
    return drawer.getAttribute('data-animation-state') === 'open';
  }

  function visibleFocusable() {
    return Array.prototype.filter.call(
      drawer.querySelectorAll(FOCUSABLE),
      function (el) {
        // offsetParent is null for display:none subtrees, which the drawer
        // uses heavily for its collapsed tier-2 / tier-3 panels.
        return el.offsetParent !== null;
      }
    );
  }

  function closeDrawer() {
    if (closeButton) {
      closeButton.click();
    } else {
      trigger.click();
    }
  }

  function onOpen() {
    lastFocused = document.activeElement;
    // The drawer carries tabindex="-1" so it can receive focus itself.
    // Focusing the container rather than the first link means screen readers
    // announce the dialog label before the menu contents.
    drawer.focus();
  }

  function onClose() {
    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    } else {
      trigger.focus();
    }
    lastFocused = null;
  }

  // Keep aria-expanded and focus in sync with whatever opens or closes the
  // drawer, including the theme's own handlers and the close button.
  var observer = new MutationObserver(function () {
    var open = isOpen();
    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');

    if (open && lastFocused === null) {
      onOpen();
    } else if (!open && lastFocused !== null) {
      onClose();
    }
  });

  observer.observe(drawer, {
    attributes: true,
    attributeFilter: ['data-animation-state']
  });

  // --- Search takeover: keep the header search triggers' aria-expanded in
  // sync. The takeover animates via the same data-animation-state mechanism
  // as the nav drawer (Core.Animations.transition, initial state 'closed').
  var takeover = document.querySelector('[data-live-search-takeover]');
  var searchButtons = document.querySelectorAll('[data-search-button]');

  if (takeover && searchButtons.length) {
    var searchObserver = new MutationObserver(function () {
      var open = takeover.getAttribute('data-animation-state') === 'open';
      searchButtons.forEach(function (btn) {
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
    searchObserver.observe(takeover, {
      attributes: true,
      attributeFilter: ['data-animation-state']
    });
  }

  document.addEventListener('keydown', function (event) {
    if (!isOpen()) return;

    if (event.key === 'Escape' || event.key === 'Esc') {
      event.preventDefault();
      closeDrawer();
      return;
    }

    if (event.key !== 'Tab') return;

    var focusable = visibleFocusable();
    if (focusable.length === 0) {
      // Nothing to tab to; keep focus on the dialog rather than escaping it.
      event.preventDefault();
      drawer.focus();
      return;
    }

    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    var active = document.activeElement;

    if (event.shiftKey && (active === first || active === drawer)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  });
})();
